import { useMemo, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import AuthLanguageSwitcher from '@/components/AuthLanguageSwitcher.jsx';
import {
  DEFAULT_LANGUAGE,
  buildLocalizedUrl,
  detectLanguageFromPath,
  getStoredLanguage,
  persistAuthLanguage,
  persistLanguage,
  resolveLanguagePriority,
} from '@/utils/languageRouting.js';

const LoginPage = () => {
  const { t, i18n } = useTranslation('dashboard');
  const { login, syncProfileLanguage } = useAuth();
  const { getLocalizedPath, language } = useLanguageNavigation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const routerNavigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', remember: false });
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const storedLanguage = useMemo(() => getStoredLanguage(), []);
  const resetSuccess = searchParams.get('reset') === 'success';

  const resolveAuthLanguage = useCallback(
    () =>
      resolveLanguagePriority(
        detectLanguageFromPath(location.pathname),
        language,
        storedLanguage,
        i18n.language,
        DEFAULT_LANGUAGE,
      ),
    [i18n.language, language, location.pathname, storedLanguage],
  );

  const registerLink = useMemo(() => {
    const queryString = searchParams.toString();
    return getLocalizedPath('/register', queryString ? { search: `?${queryString}` } : undefined);
  }, [getLocalizedPath, searchParams]);
  const resetLink = useMemo(() => getLocalizedPath('/password-reset'), [getLocalizedPath]);

  const getSafeReturnUrl = useCallback((selectedLanguage) => {
    const rawReturnUrl = searchParams.get('returnUrl');
    if (!rawReturnUrl) return null;

    try {
      const url = new URL(rawReturnUrl, window.location.origin);
      if (url.origin !== window.location.origin) {
        return null;
      }
      const langInReturn = detectLanguageFromPath(url.pathname);
      const pathWithQuery = `${url.pathname}${url.search}${url.hash}`;

      if (langInReturn) {
        return pathWithQuery;
      }

      return buildLocalizedUrl(url.pathname, selectedLanguage, url.search, url.hash);
    } catch (e) {
      console.warn('Некорректный returnUrl', e);
      return null;
    }
  }, [searchParams]);


  const getLoginErrorMessage = useCallback((errorCode) => {
    if (errorCode === 'ACCOUNT_BLOCKED') {
      return t('auth.login.errors.accountBlocked');
    }
    return null;
  }, [t]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPending(true);
    setError('');
    try {
      const targetLanguage = resolveAuthLanguage();
      const authData = await login(form.email, form.password, form.remember);
      persistLanguage(targetLanguage);
      persistAuthLanguage(targetLanguage);
      if (i18n.language !== targetLanguage) {
        i18n.changeLanguage(targetLanguage);
      }
      await syncProfileLanguage(targetLanguage, authData?.token);
      const targetAfterLogin =
        getSafeReturnUrl(targetLanguage) ||
        getLocalizedPath('/dashboard/create-game', { language: targetLanguage });
      routerNavigate(targetAfterLogin, { replace: true });
    } catch (err) {
      setError(getLoginErrorMessage(err?.message) || err.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={handleSubmit}>
        <AuthLanguageSwitcher />
        <h1>{t('auth.login.title')}</h1>
        <p className="auth-subtitle">{t('auth.login.subtitle')}</p>
        <label>
          {t('auth.login.email')}
          <input
            name="email"
            type="email"
            placeholder={t('auth.login.placeholders.email')}
            value={form.email}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          {t('auth.login.password')}
          <input
            name="password"
            type="password"
            placeholder={t('auth.login.placeholders.password')}
            value={form.password}
            onChange={handleChange}
            required
          />
        </label>
        <div className="checkbox-field auth-remember">
          <input
            id="auth-remember"
            name="remember"
            type="checkbox"
            checked={form.remember}
            onChange={handleCheckboxChange}
          />
          <label htmlFor="auth-remember">{t('auth.login.remember')}</label>
        </div>
        {error && <p className="auth-error">{error}</p>}
        {resetSuccess && <p className="auth-info">{t('auth.login.resetSuccess')}</p>}
        <button type="submit" disabled={pending}>
          {pending ? t('auth.login.submitting') : t('auth.login.submit')}
        </button>
        <p className="auth-helper">
          {t('auth.login.helper')} <Link to={registerLink}>{t('auth.login.register')}</Link>
        </p>
        <p className="auth-helper">
          <Link to={resetLink}>{t('auth.login.forgot')}</Link>
        </p>
      </form>
    </div>
  );
};

export default LoginPage;
